import type { NovelSummary } from "../apiClient.js";

/**
 * Which script a novel is written in — the stand-in for its language on the
 * surfaces that pick novels for a reader (the landing's featured novel and
 * showcase, the ending's "Read another story"). A catalog summary carries no
 * language, so the script of its title and description decides: someone reading
 * in English is not offered a Russian story while an English one is left.
 */
export type Script = "latin" | "cyrillic" | "cjk";

// Languages written in Cyrillic or in CJK scripts; any other code counts as Latin.
const CYRILLIC_LANGUAGES = new Set(["ru", "uk", "be", "bg", "sr", "mk", "kk", "ky", "mn", "tg"]);
const CJK_LANGUAGES = new Set(["zh", "ja", "ko"]);

/** The script a language code (a UI language, a novel's `language`) is written in. */
export function scriptOfLanguage(code: string): Script {
  const base = code.trim().toLowerCase().split(/[-_]/)[0] ?? "";
  if (CYRILLIC_LANGUAGES.has(base)) return "cyrillic";
  return CJK_LANGUAGES.has(base) ? "cjk" : "latin";
}

/** The script most letters of `text` are written in; null when it has no letters. */
export function dominantScript(text: string): Script | null {
  const count = (pattern: RegExp) => text.match(pattern)?.length ?? 0;
  const tally: Array<[Script, number]> = [
    ["latin", count(/\p{Script=Latin}/gu)],
    ["cyrillic", count(/\p{Script=Cyrillic}/gu)],
    ["cjk", count(/[\p{Script=Han}\p{Script=Hiragana}\p{Script=Katakana}\p{Script=Hangul}]/gu)]
  ];
  const [script, letters] = tally.reduce((best, entry) => (entry[1] > best[1] ? entry : best));
  return letters > 0 ? script : null;
}

/** The script a catalog novel is written in, read from its title and description. */
export function novelScript(novel: Pick<NovelSummary, "title" | "description">): Script | null {
  return dominantScript(`${novel.title} ${novel.description}`);
}
